import type { TFunction } from 'i18next';
import { isApiError } from '@shared/api/errors/errors';
import { readApiError } from '@shared/api/errors/read-api-error';
import { i18n } from '@shared/i18n';
import { formatDateTime, retryAfterMinutes } from '@shared/lib/format-date-time';

export const getApiErrorMessage = (
  error: unknown,
  t: TFunction,
  fallbackKey: string
): string => {
  if (isApiError(error)) {
    const parsed = readApiError(error);

    if (parsed?.code === 'WITHDRAWALS_LOCKED') {
      return t('withdrawal.lock.until', {
        ns: 'cabinet',
        datetime: formatDateTime(parsed.lockedUntil ?? '', i18n.language),
      });
    }

    if (parsed?.code === 'WITHDRAWALS_STOPPED') {
      return t('withdrawals.errors.stopped', { ns: 'cabinet' });
    }

    if (parsed?.code === 'WITHDRAWAL_LIMIT_EXCEEDED') {
      return t('withdrawals.errors.limit', { ns: 'cabinet' });
    }

    if (parsed?.code === 'WITHDRAWAL_2FA_LOCKED') {
      return t('withdrawals.errors.locked2fa', { ns: 'cabinet' });
    }

    if (parsed?.code === 'OTP_INVALID') {
      return t('withdrawals.errors.otp', { ns: 'cabinet' });
    }

    if (parsed?.code === 'ADDRESS_COOLING_PERIOD') {
      return t('withdrawals.errors.cooling', { ns: 'cabinet' });
    }

    if (parsed?.code === 'ADDRESS_AML_BLOCKED') {
      return t('security.addressAmlBlockedHint', { ns: 'cabinet' });
    }

    if (parsed?.code === 'AUTO_WITHDRAWAL_RULE_CHANGED') {
      return t('workspace.ruleChanged', {
        ns: 'cabinet',
        address: parsed.currentAddress ?? '',
      });
    }

    if (
      parsed?.code &&
      [
        'COMPLIANCE_REVIEWER_REQUIRED',
        'COMPLIANCE_SELF_REVIEW',
        'COMPLIANCE_DECISION_NOT_ALLOWED',
        'COMPLIANCE_DECISION_PENDING',
        'COMPLIANCE_AML_RESULT_REQUIRED',
        'COMPLIANCE_REASON_REQUIRED',
        'COMPLIANCE_SECOND_APPROVER_REQUIRED',
        'COMPLIANCE_DECISION_RESOLVED',
        'COMPLIANCE_SELF_GRANT',
        'COMPLIANCE_INFO_NOT_REQUESTED',
        'COMPLIANCE_MESSAGES_LIMIT',
        'ADDRESS_NOT_BLOCKED',
        'AML_POLICY_VERSION_CONFLICT',
        'AML_POLICY_INVALID',
        'AML_POLICY_SELF_APPROVAL',
        'AML_BILLING_VERSION_CONFLICT',
        'AML_CHARGES_MONTH_OPEN',
      ].includes(parsed.code)
    ) {
      return t(`compliance.errors.${parsed.code}`, { ns: 'cabinet' });
    }

    if (parsed?.code === 'WITHDRAWAL_AML_REJECTED') {
      return t('withdrawals.errors.aml', { ns: 'cabinet' });
    }

    if (parsed?.code === 'WITHDRAWAL_AML_UNAVAILABLE') {
      return t('withdrawals.errors.amlDown', { ns: 'cabinet' });
    }

    if (parsed?.code === 'WITHDRAWAL_ALREADY_ACTIVE') {
      return t('withdrawals.errors.already', { ns: 'cabinet' });
    }

    if (parsed?.code === 'WITHDRAWAL_INTENT_EXPIRED' || parsed?.code === 'WITHDRAWAL_CHALLENGE_EXPIRED') {
      return t('withdrawals.errors.expired', { ns: 'cabinet' });
    }

    if (parsed?.code === 'ADDRESS_NOT_WHITELISTED') {
      if (parsed.reason === 'pending') {
        return t('security.errors.addressPending', {
          ns: 'cabinet',
          datetime: formatDateTime(parsed.activeFrom ?? '', i18n.language),
        });
      }
      return t('security.errors.addressNotListed', { ns: 'cabinet' });
    }

    if (parsed?.code === 'WITHDRAWAL_NOT_CANCELLABLE') {
      return t('security.errors.withdrawalNotCancellable', { ns: 'cabinet' });
    }

    if (parsed?.code === 'TOO_MANY_REQUESTS') {
      return t('recovery.tooManyRequests', {
        ns: 'auth',
        minutes: retryAfterMinutes(parsed.retryAfterSec ?? 60),
      });
    }

    if (error.status && error.status >= 500) {
      return t('common.serverError', { ns: 'common' });
    }

    if (error.status === 403) {
      return t('common.forbidden', { ns: 'common' });
    }

    if (error.status === 401) {
      return t('common.unauthorized', { ns: 'common' });
    }

    if (error.message === 'Существует активный вывод') {
      return t('withdrawals.errors.already', { ns: 'cabinet' });
    }

    if (error.message && error.message !== 'An error occurred') {
      return error.message;
    }
  }

  return t(fallbackKey);
};
